"use client"

import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import useIsMobile from "@/lib/config/lib/use-is-mobile"
import { DefaultImage, handleImageError } from "@/lib/external-components"
import { generateImagePath } from "@/lib/utils"
import { TourItem } from "@/types/types"
import { getFormattedPriceParts } from "@/utils/formatPrice"
import { ComponentConfig } from "@measured/puck"
import { Spin } from "antd"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import { Autoplay, Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { fetchToursV2 } from "../../../../../tour/services/tours"

export interface ToursCarouselProps {
  categoryId?: string
  tag?: string
  isMostPopular?: boolean
  autoplay?: boolean
  slidesPerView?: number
  spaceBetween?: number
  title?: string
  buttonText?: string
  buttonUrl?: string
}

export const RenderConfig: ComponentConfig<ToursCarouselProps> = {
  render: ({ categoryId, tag, isMostPopular, autoplay, slidesPerView, spaceBetween, title, buttonText, buttonUrl }) => {
    const [tours, setTours] = useState<TourItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const isMobile = useIsMobile()

    const loadTours = async () => {
      setLoading(true)
      try {
        let params: Record<string, any> = {}
        let shouldLoad = false

        if (categoryId === "") {
          shouldLoad = true
        } else if (categoryId) {
          params.category = Number(categoryId)
          shouldLoad = true
        }

        if (isMostPopular) {
          params.is_most_popular = true
          shouldLoad = true
        }

        if (tag) {
          params.tag = tag
          shouldLoad = true
        }

        if (!shouldLoad) {
          setTours([])
          setLoading(false)
          return
        }

        const res = await fetchToursV2(params, undefined)

        const data = (res as { data: TourItem[] }).data
        setTours(data || [])
      } catch (e) {
        console.error("Error loading tours:", e)
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      if (categoryId !== undefined || isMostPopular !== undefined || tag !== undefined) {
        loadTours()
      }
    }, [categoryId, isMostPopular, tag])

    if (loading) {
      return (
        <div className="flex items-center justify-center h-[300px] w-full">
          <Spin />
        </div>
      )
    }

    return (
      <div className="w-full flex flex-col sm:gap-16 gap-6 sm:pl-0 pl-4">
        {title && !buttonText ? (
          <h2 className="sm:text-[40px] sm:leading-[54px] text-[28px] leading-[36px] text-[#0C9443] font-bold text-center">
            {title}
          </h2>
        ) : (
          <div className="flex items-center sm:justify-between justify-center">
            {title && (
              <h2 className="sm:text-[40px] sm:leading-[54px] text-[28px] leading-[36px] text-[#0C9443] font-bold text-center">
                {title}
              </h2>
            )}
            {buttonText && buttonUrl && (
              <Link
                href={buttonUrl}
                className="sm:block hidden text-base leading-[27px] font-semibold text-[#0B9444] py-2 px-5 border border-[#0B9444] rounded-full hover:bg-[#0B9444] hover:text-white transition-all duration-300"
              >
                {buttonText}
              </Link>
            )}
          </div>
        )}
        <div className="w-full">
          {(!tours || tours.length === 0) && (
            <div className="w-full flex items-center justify-center py-12 text-[#8B8B90]">
              {t("Currently no tours available")}
            </div>
          )}
          {tours && tours.length > 0 && (
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={isMobile ? 32 : 40}
              slidesPerView={isMobile ? slidesPerView || 1.3 : slidesPerView || 3}
              navigation={{
                nextEl: ".enroll-swiper-next",
                prevEl: ".enroll-swiper-prev",
              }}
              onSlideChange={swiper => setCurrentIndex(swiper.realIndex)}
              {...(autoplay && {
                autoplay: {
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: false,
                },
              })}
              loop={true}
              grabCursor={true}
            >
              {tours.map((tour, idx) => {
                const attr = tour?.attributes
                const img = attr?.feature_img?.[0]
                const imgAlt =
                  typeof img === "object"
                    ? (img as { alternativeText?: string }).alternativeText
                    : attr?.title || `Tour image ${idx + 1}`
                const imageUrl = img
                  ? typeof img === "string"
                    ? generateImagePath(img, "medium")
                    : generateImagePath((img as { url: string }).url, "medium")
                  : DefaultImage

                const priceRaw = attr?.price_overview || t("Contact us for price")
                const { priceMain } = getFormattedPriceParts(priceRaw)
                const currentLocale = typeof document !== "undefined" ? document.documentElement.lang || "en" : "en"
                const isVI = currentLocale?.toLowerCase().startsWith("vi")
                const slug = attr?.slug ? `/tours/${attr.slug}` : "#"

                return (
                  <SwiperSlide key={tour.id || idx}>
                    <Link
                      href={currentLocale === "vi" ? `/vi/tours/${attr?.slug}` : `/tours/${attr?.slug}`}
                      className="group flex flex-col h-full w-full rounded-2xl overflow-hidden bg-white shadow md:hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative flex-shrink-0 overflow-hidden">
                        <div className="w-full h-[229px]">
                          <Image
                            src={imageUrl}
                            alt={imgAlt || attr?.name || `Tour ${idx + 1}`}
                            width={300}
                            height={229}
                            onError={e =>
                              handleImageError(e, typeof img === "string" ? img : (img as { url: string })?.url)
                            }
                            className="w-full h-full object-cover absolute z-[11] transition-transform duration-500 md:group-hover:scale-110"
                          />
                        </div>
                        {attr?.is_most_popular && (
                          <div className="absolute top-0 right-0 p-2 bg-[#C9E4A5] z-20 transition-all duration-300 md:group-hover:bg-[#B8D494]">
                            <p className="text-[0.625rem] leading-3 font-semibold text-[#0B9444] lg:text-xs lg:leading-3">
                              {t("Most Popular")}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="bg-[#0B9444] text-white flex flex-col p-6 flex-1 transition-all duration-300 md:group-hover:bg-[#0A8139]">
                        <div className="flex flex-col gap-4 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="flex bg-white rounded-[6px] py-[6px] px-2 items-center gap-2">
                              <Image src={Images.iconDistance} alt="distance" width={16} height={16} />
                              <p className="text-[10px] lg:text-xs leading-4 font-medium text-[#0C9443] uppercase">
                                {attr?.distance}
                              </p>
                            </div>
                            <div className="flex bg-[#C9F0D9] rounded-[6px] py-[6px] px-2 items-center gap-2">
                              <Image src={Images.iconDuration} alt="duration" width={16} height={16} />
                              <p className="text-[10px] lg:text-xs leading-4 font-medium text-[#0C9443] uppercase">
                                {attr?.duration}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 flex-1">
                            <h3 className="text-base lg:text-lg leading-[28px] lg:leading-[30px] font-semibold transition-all duration-300 md:group-hover:text-[#C9E4A5]">
                              {attr?.name}
                            </h3>
                            <p className="text-sm lg:text-base leading-[25px] lg:leading-[27px] text-white/80 line-clamp-3 transition-all duration-300 md:group-hover:text-white">
                              {attr?.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 sm:flex-row justify-between text-sm font-semibold mt-4">
                          <div className="flex gap-1 items-center">
                            <p className="text-lg lg:text-xl leading-[17px] lg:leading-[19px] font-semibold transition-all duration-300 md:group-hover:scale-110 md:group-hover:text-[#C9E4A5]">
                              {!isVI ? "$" : ""}
                              {priceMain}
                              {isVI ? " đ" : ""}
                            </p>
                            <span className="text-xs lg:text-sm leading-[17px] lg:leading-[19px] text-white/80 transition-all duration-300 md:group-hover:text-white">
                              {" "}
                              / {t("person")}
                            </span>
                          </div>
                          <span className="text-sm lg:text-base leading-[25px] lg:leading-[27px] font-semibold underline transition-all duration-300 md:group-hover:translate-x-1 md:group-hover:text-[#C9E4A5]">
                            {t("See details")}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          )}
          {/* Desktop navigation buttons */}
          {tours && tours.length > 0 && (
            <div className="hidden sm:flex items-center justify-center gap-2 pt-16">
              <button
                className="enroll-swiper-prev rounded-full bg-[#EBEDF0] p-3 cursor-pointer"
                aria-label="Previous slide"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="enroll-swiper-next rounded-full bg-[#EBEDF0] p-3 cursor-pointer"
                aria-label="Next slide"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Mobile dots navigation */}
          {tours && tours.length > 0 && (
            <div className="flex items-center w-full justify-center sm:hidden">
              <div className="flex justify-center mt-4 bg-[#242F3E0F] py-[10px] px-2 rounded-full">
                {tours.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-2 mx-[5px] rounded-full transition-all ${
                      currentIndex === index ? "bg-[#000] w-6" : "bg-[#D9D9D9]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          {buttonText && buttonUrl && (
            <Link
              href={buttonUrl}
              className="sm:hidden mt-10 block mx-4 text-base text-center leading-[27px] font-semibold text-[#0B9444] py-2 px-5 border border-[#0B9444] rounded-full hover:bg-[#0B9444] hover:text-white transition-all duration-300"
            >
              {buttonText}
            </Link>
          )}
        </div>
      </div>
    )
  },
}
